import { useNavigate } from 'react-router-dom';

import { CareerPath } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme, Chip } from '@mui/material';

const STRINGS = appStrings.modules.majors.descriptionSidebar;

type CareerPathPillsProps = Readonly<{
  highRelevanceCareerPaths: CareerPath[];
}>;

export const CareerPathPills = ({ highRelevanceCareerPaths }: CareerPathPillsProps) => {
  const navigate = useNavigate();

  const handleCareerPathClick = (careerPathId: number) => {
    navigate(`${APP_ROUTE.CAREER_PATHS}/${careerPathId}`);
  };

  return (
    <Box sx={sectionSx}>
      <Box sx={labelContainerSx}>
        <Typography variant="text-md-semibold" sx={labelSx}>
          {STRINGS.careerPaths}
        </Typography>
      </Box>
      {highRelevanceCareerPaths.length > 0 ? (
        <Box sx={chipsContainerSx}>
          {highRelevanceCareerPaths.map((careerPath) => (
            <Chip
              key={careerPath.id}
              id={`major-career-path-${careerPath.id}-chip`}
              variant="pill"
              label={careerPath.name}
              size="md"
              sx={careerPathChipSx}
              type="leading"
              onClick={() => handleCareerPathClick(careerPath.id)}
              clickable
            />
          ))}
        </Box>
      ) : (
        <Typography variant="text-sm-regular" sx={noDataSx}>
          {STRINGS.nA}
        </Typography>
      )}
    </Box>
  );
};

const careerPathChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
    ...typography['text-xs-medium'],
  },
  alignSelf: 'flex-start',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/100'],
  },
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  minWidth: '100%',
  maxWidth: '100%',
  '@container (min-width: 900px)': {
    minWidth: '280px',
    maxWidth: '280px',
  },
});

const labelContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/600'],
});

const noDataSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontStyle: 'italic',
});

const chipsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});
